// Role-aware navigation data. Route-group layouts belong to this task alone (TASKS.md §4 rule 7)
// and no later frontend task may edit this file, so the paths below are this task's contract with
// every later frontend task: T-21/T-32 (investor), T-22 (end user), T-23 (PPA), T-28 (ops), T-24
// (superadmin) must create their pages at the paths named here. The investor paths are not a
// guess — they are the exact folder names TASKS.md's own Files column already fixes for T-21 and
// T-32 (`(investor)/kyc`, `edukasi`, `investasi`, `portofolio`, `distribusi`, `saldo`, `rekening`,
// `pajak`, each a bare top-level segment because a Next.js route-group folder name contributes
// nothing to the URL). The other four groups get no such fixed slugs from TASKS.md, so this file
// assigns each a stable `/<group>/…` prefix from its own console's stated responsibilities
// (TASKS.md T-22, T-23, T-24, T-28 `done_when`), which also keeps every group's paths disjoint
// from investor's un-prefixed ones and from the public paths in `middleware.ts`.
// Relative, not `@/` — `frontend/vitest.config.ts` (T-03's, frozen) has no path-alias resolution.
import type { components } from '../../lib/api/client';

export type Role = components['schemas']['Role'];

export interface NavItem {
  readonly href: string;
  readonly label: string;
}

/**
 * DONE WHEN: "the role navigation renders no money action for `ops`" (SPEC: ops verifies and
 * proposes, never approves or moves money — US-36…US-39). Every ops item below is a read/verify/
 * propose action; none disburses, approves or transfers money. `navigation.test.ts` asserts none
 * of these words appears in the ops list.
 */
export const NAV_BY_ROLE: Readonly<Record<Role, readonly NavItem[]>> = {
  investor: [
    { href: '/kyc', label: 'Verifikasi identitas' },
    { href: '/edukasi', label: 'Edukasi sebelum membeli' },
    { href: '/investasi', label: 'Investasi' },
    { href: '/portofolio', label: 'Portofolio' },
    { href: '/distribusi', label: 'Distribusi' },
    { href: '/saldo', label: 'Saldo & penarikan' },
    { href: '/rekening', label: 'Rekening bank' },
    { href: '/pajak', label: 'Bukti potong pajak' },
  ],
  end_user: [
    { href: '/rumah/pengajuan', label: 'Pengajuan' },
    { href: '/rumah/kontrak', label: 'Akad & jadwal angsuran' },
    { href: '/rumah/pembayaran', label: 'Pembayaran' },
    { href: '/rumah/progres', label: 'Progres kepemilikan' },
    { href: '/rumah/pelunasan-dini', label: 'Pelunasan dipercepat' },
  ],
  ppa_staff: [
    { href: '/ppa/aset', label: 'Pendaftaran aset' },
    { href: '/ppa/kelayakan', label: 'Kelayakan calon pemilik rumah' },
    { href: '/ppa/pembayaran', label: 'Konfirmasi pembayaran' },
    { href: '/ppa/rekonsiliasi', label: 'Rekonsiliasi kas angsuran' },
    { href: '/ppa/distribusi', label: 'Usulan distribusi' },
    { href: '/ppa/settlement', label: 'Usulan settlement & terminasi' },
    { href: '/ppa/akun-end-user', label: 'Buat akun end user' },
  ],
  ops: [
    { href: '/ops/kyc', label: 'Antrean KYC investor' },
    { href: '/ops/apu-ppt', label: 'Penyaringan APU-PPT' },
    { href: '/ops/rekening-investor', label: 'Verifikasi rekening investor' },
    { href: '/ops/dokumen-developer', label: 'Verifikasi dokumen developer' },
    { href: '/ops/listing', label: 'Checklist siap listing' },
    { href: '/ops/rekonsiliasi-escrow', label: 'Rekonsiliasi escrow' },
    { href: '/ops/permintaan-data', label: 'Permintaan data subjek' },
    { href: '/ops/pengalihan-token', label: 'Usulan pengalihan token' },
  ],
  superadmin: [
    { href: '/admin/pengaturan', label: 'Parameter platform' },
    { href: '/admin/proyek', label: 'Siklus proyek' },
    { href: '/admin/persetujuan', label: 'Persetujuan' },
    { href: '/admin/audit', label: 'Jejak audit' },
    { href: '/admin/laporan-kas', label: 'Laporan keseimbangan kas' },
  ],
} as const;

/** The account area's own sub-navigation, shared by every role (TASKS.md `(akun)/**`, this task). */
export const ACCOUNT_NAV_ITEMS: readonly NavItem[] = [
  { href: '/akun', label: 'Profil & sesi' },
  { href: '/akun/privasi', label: 'Privasi' },
];

const ROLE_LABEL: Readonly<Record<Role, string>> = {
  investor: 'Investor',
  end_user: 'End user',
  ppa_staff: 'PPA',
  ops: 'Ops',
  superadmin: 'Superadmin',
};

export function roleLabel(role: Role): string {
  return ROLE_LABEL[role];
}
